/**
 * TG-B — transaction history store. Lists past (stopped) transactions for a
 * station and loads a selected transaction's persisted progress series for the
 * drill-in chart. Reads the transaction + transactionSamples repos so the UI
 * layer doesn't touch persistence directly.
 */
import { create } from 'zustand';
import { db } from '@/persistence/dexie/db';
import {
  TransactionRepositoryDexie,
  TransactionSamplesRepositoryDexie,
} from '@/persistence/dexie/repositories';
import type { TransactionSnapshot } from '@/domain';
import type { StationId } from '@/domain/types';
import type { TxSample } from '@/lib/txTelemetry';

const txRepo = new TransactionRepositoryDexie(db);
const samplesRepo = new TransactionSamplesRepositoryDexie(db);

export interface TransactionHistoryStore {
  readonly transactions: readonly TransactionSnapshot[];
  readonly status: 'idle' | 'ready';
  readonly selectedTxId: number | null;
  /** null = nothing selected; [] = selected but no series was recorded. */
  readonly selectedSamples: readonly TxSample[] | null;
  load(stationId: StationId): Promise<void>;
  select(stationId: StationId, transactionId: number): Promise<void>;
  clearSelection(): void;
}

export const useTransactionHistoryStore = create<TransactionHistoryStore>((set) => ({
  transactions: [],
  status: 'idle',
  selectedTxId: null,
  selectedSamples: null,

  async load(stationId): Promise<void> {
    const all = await txRepo.byStation(stationId);
    const past = all
      .filter((t) => t.stoppedAt !== undefined)
      .slice()
      .sort((a, b) => b.startedAt - a.startedAt);
    set({ transactions: past, status: 'ready', selectedTxId: null, selectedSamples: null });
  },

  async select(stationId, transactionId): Promise<void> {
    const samples = await samplesRepo.get(stationId, transactionId);
    set({ selectedTxId: transactionId, selectedSamples: samples ?? [] });
  },

  clearSelection(): void {
    set({ selectedTxId: null, selectedSamples: null });
  },
}));
